import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import { useGuestSaves } from "@/hooks/useGuestSaves";
import { Button } from "@/components/ui/button";

const SHOWN_KEY = "vt_re_engage_shown";
const DELAY_MS = 30_000;

/**
 * Re-engagement toast: 30s after first guest save, suggest creating an account.
 * Mount once near the app root. Shows max once per session.
 */
const GuestReEngageToast = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const saves = useGuestSaves();
  const timerRef = useRef<number | null>(null);
  const armedRef = useRef(false);

  useEffect(() => {
    if (user) return;
    if (saves.totalCount === 0) return;
    if (sessionStorage.getItem(SHOWN_KEY) === "1") return;
    if (armedRef.current) return;
    armedRef.current = true;

    timerRef.current = window.setTimeout(() => {
      if (sessionStorage.getItem(SHOWN_KEY) === "1") return;
      sessionStorage.setItem(SHOWN_KEY, "1");
      toast({
        title: t("guestRescue.toastTitle"),
        description: t("guestRescue.toastBody"),
        action: (
          <Button
            size="sm"
            onClick={() =>
              navigate(`/signup?redirect=${encodeURIComponent("/trips?restore=1")}`)
            }
          >
            {t("guestRescue.toastCta")}
          </Button>
        ) as any,
      });
    }, DELAY_MS);

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [user, saves.totalCount, navigate, t]);

  return null;
};

export default GuestReEngageToast;
